import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import './FeaturedHeroCarousel.css';
import spotlightService from '../services/SpotlightService';
import cmsService, { extractGigcasterIdFromChannel } from '../services/CMSService';
import {
  preloadSlimEventDataForChannels,
  formatChannelEventDate,
  getChannelEventDateValue,
} from '../utils/channelDateUtils';
import PDPModal from './PDPModal';
import { useWatchButtonState } from '../hooks/useWatchButtonState';
import { useLiveEventStatus } from '../hooks/useLiveEventStatus';
import LiveBadge from './LiveBadge';
import { FALLBACK_IMAGE_SRC } from '../utils/ImageUtils';

const HeroSlideWatchButton = ({ channel, onClick, onWatchIn, cmsReady }) => {
  const slug = channel?.slug || channel?.strSlug;
  const gigcasterId = useMemo(
    () =>
      extractGigcasterIdFromChannel(channel) ||
      cmsService.getCachedGigcasterIdForSlug(slug) ||
      null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [channel, slug, cmsReady],
  );
  const slimEventData = useMemo(
    () => (gigcasterId ? cmsService.getSlimEventData(gigcasterId) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [gigcasterId, cmsReady],
  );
  const watchState = useWatchButtonState(channel, slimEventData);

  if (watchState.buttonState === 'buy-now') {
    return (
      <button
        type="button"
        className="featured-hero-cta flex items-center px-6 py-3 font-bold text-primary font-display text-lg uppercase tracking-wide bg-accent hover:bg-accent-hover"
        onClick={onClick}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          className="w-5 h-5 mr-2"
        >
          <path
            fillRule="evenodd"
            d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
            clipRule="evenodd"
          />
        </svg>
        Buy Now
      </button>
    );
  }

  if (watchState.buttonState === 'watch-in' && watchState.countdown) {
    const cd = watchState.countdown;
    return (
      <button
        type="button"
        className="featured-hero-cta flex items-center px-6 py-3 font-bold text-primary font-display text-lg uppercase tracking-wide bg-accent hover:bg-accent-hover"
        onClick={onWatchIn || onClick}
      >
        <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
          <path d="M6 4l10 6-10 6V4z" />
        </svg>
        <span className="mr-2">Watch In</span>
        <span className="inline-flex items-center bg-black/80 text-white px-2 py-0.5 text-sm font-display tracking-wider rounded">
          {Number(cd.days) > 0 && <>{cd.days}:</>}
          {cd.hours}:{cd.minutes}:{cd.seconds}
        </span>
      </button>
    );
  }

  // Scheduled time passed but the stream isn't live yet — no dead button.
  if (watchState.buttonState === 'starting-soon') {
    return (
      <button
        type="button"
        disabled
        className="featured-hero-cta flex items-center px-6 py-3 font-bold text-primary font-display text-lg uppercase tracking-wide bg-accent/60 cursor-default"
      >
        Starting Soon…
      </button>
    );
  }

  return (
    <button
      type="button"
      className="featured-hero-cta flex items-center px-6 py-3 font-bold text-primary font-display text-lg uppercase tracking-wide bg-accent hover:bg-accent-hover"
      onClick={onClick}
    >
      {watchState.isLive && <LiveBadge className="mr-2" />}
      Watch Now
    </button>
  );
};

const HeroSlideEventDate = ({ channel, cmsReady }) => {
  const slug = channel?.slug || channel?.strSlug;
  const gigcasterId = useMemo(
    () =>
      extractGigcasterIdFromChannel(channel) ||
      cmsService.getCachedGigcasterIdForSlug(slug) ||
      null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [channel, slug, cmsReady],
  );
  const slimEventData = useMemo(
    () => (gigcasterId ? cmsService.getSlimEventData(gigcasterId) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [gigcasterId, cmsReady],
  );

  // Live start_time ?? event_date overrides the cached CMS date.
  const live = useLiveEventStatus(gigcasterId);
  const rawDate = live.effectiveDate ?? getChannelEventDateValue(channel, slimEventData);
  const formatted = formatChannelEventDate(rawDate);

  if (!formatted) return null;

  return <p className="mb-4 text-lg text-white/80 font-display tracking-wide">{formatted}</p>;
};

const FeaturedHeroCarousel = ({ category }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [channels, setChannels] = useState(category?.channels || []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [showPDPModal, setShowPDPModal] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const carouselRef = useRef(null);
  const sliderRef = useRef(null);
  const hasAttemptedFetch = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { root: null, rootMargin: '100px', threshold: 0.1 },
    );

    if (carouselRef.current) {
      const currentRef = carouselRef.current;
      observer.observe(currentRef);
      return () => observer.unobserve(currentRef);
    }
    return undefined;
  }, []);

  useEffect(() => {
    const fetchChannels = async () => {
      if (!category || channels.length > 0 || loading || !isVisible) return;
      if (Array.isArray(category.channels) && category.channels.length > 0) {
        setChannels(category.channels);
        return;
      }
      // Only hit the network once. An empty or failed 'featured' response leaves
      // `channels` empty while `loading` toggles true→false, which re-arms this
      // effect and re-fetches the endpoint forever. Latch the network path
      // (mirrors useCategoryRail's hasAttemptedFetch guard); a page reload retries.
      if (hasAttemptedFetch.current) {
        return;
      }
      hasAttemptedFetch.current = true;
      try {
        setLoading(true);
        setError(null);
        const response = await spotlightService.getCategoryChannelsBySlug('featured');
        let channelsData = [];
        if (response?.channels && Array.isArray(response.channels)) {
          channelsData = response.channels;
        }
        setChannels(channelsData);
      } catch (err) {
        console.error('FeaturedHeroCarousel: Error loading channels:', err);
        setError("We couldn't load featured content right now. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchChannels();
  }, [category, channels.length, loading, isVisible]);

  // Preload slim event data for all channels (so watch buttons can detect future events)
  const [cmsReady, setCmsReady] = useState(0);
  useEffect(() => {
    if (channels.length === 0) return;
    let isMounted = true;

    const preload = async () => {
      const count = await preloadSlimEventDataForChannels(channels);
      if (isMounted && count > 0) {
        setCmsReady(v => v + 1);
      }
    };

    preload();
    return () => {
      isMounted = false;
    };
  }, [channels]);

  const sliderSettings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 8000,
    arrows: true,
    beforeChange: (current, next) => setCurrentIndex(next),
  };

  const handleWatchNowClick = channel => {
    if (!channel) return;

    if (channel.subscription_access?.unlocked === false) {
      const channelId = channel.dspro_id || channel._id;
      if (channelId) {
        navigate(`/checkout/${channelId}`, { state: { channel } });
      }
      return;
    }

    const channelSlug = channel.slug || channel.permalink || channel._id || channel.id;
    navigate(`/watch/${channelSlug}`, {
      state: { from: location.pathname, channel },
    });
  };

  const handleLearnMoreClick = channel => {
    setSelectedChannel(channel);
    setShowPDPModal(true);
  };

  const handleClosePDPModal = () => {
    setShowPDPModal(false);
    setSelectedChannel(null);
  };

  if (loading) {
    return (
      <div className="featured-hero-carousel relative mb-8 bg-primary" ref={carouselRef}>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="flex flex-col items-center animate-pulse">
            <div className="w-full h-64 max-w-3xl mb-4 rounded-lg bg-dark-gray" />
            <div className="w-64 h-8 mb-2 rounded-lg bg-dark-gray" />
            <div className="w-48 h-4 rounded-lg bg-dark-gray" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="featured-hero-carousel relative mb-8 bg-primary flex items-center justify-center"
        ref={carouselRef}
      >
        <div className="text-center text-red-400">
          <p>{error}</p>
          <p className="mt-2 text-sm text-white/50">Check browser console for details</p>
        </div>
      </div>
    );
  }

  if (!channels.length) {
    return (
      <div
        className="featured-hero-carousel relative mb-8 bg-primary flex items-center justify-center"
        ref={carouselRef}
      >
        <div className="text-center text-white/50">No featured content available</div>
      </div>
    );
  }

  return (
    <div className="featured-hero-carousel relative" ref={carouselRef}>
      {channels.length > 0 ? (
        <Slider {...sliderSettings} ref={sliderRef}>
          {channels.map((channel, index) => {
            const slideBackgroundImage =
              channel.wallpaper && channel.wallpaper !== ''
                ? channel.wallpaper
                : channel.image?.wallpaper && channel.image.wallpaper !== ''
                  ? channel.image.wallpaper
                  : // `spotlight_poster` (CMS "Key Art") is the 2:3 portrait asset and
                    // must not fill this full-bleed hero — wallpaper is often null.
                    channel.poster ||
                    channel.image?.poster ||
                    FALLBACK_IMAGE_SRC;

            const slideTitle = channel.title || channel.name || 'Untitled Channel';

            const slideYear =
              channel.year ||
              channel.release_year ||
              channel.production_year ||
              channel.video?.year ||
              '';

            let slideDuration = '';
            if (channel.duration) {
              if (typeof channel.duration === 'number') {
                const hours = Math.floor(channel.duration / 3600);
                const minutes = Math.floor((channel.duration % 3600) / 60);
                slideDuration = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
              } else {
                slideDuration = channel.duration;
              }
            } else if (channel.runtime) {
              slideDuration = channel.runtime;
            } else if (channel.video?.duration) {
              const videoDuration = channel.video.duration;
              if (typeof videoDuration === 'number') {
                const hours = Math.floor(videoDuration / 3600);
                const minutes = Math.floor((videoDuration % 3600) / 60);
                slideDuration = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
              } else {
                slideDuration = videoDuration;
              }
            }

            const slideGenres = Array.isArray(channel.genres) ? channel.genres : [];
            const slideGenreText = slideGenres.join(', ');
            const slideRating = channel.rating || '';
            const slideProgramType = channel.programming_type || '';

            const metadataParts = [];
            if (slideYear) metadataParts.push(slideYear);
            if (slideDuration) metadataParts.push(slideDuration);
            if (slideGenreText) metadataParts.push(slideGenreText);

            const channelLogo =
              channel.image?.channel_logo ||
              channel.channel_logo ||
              channel.image?.logo ||
              channel.logo ||
              null;

            return (
              <div key={`${channel._id || index}`} className="relative h-[100vh]">
                {/* Background Image with Gradient Overlay */}
                <div
                  className="absolute inset-0 bg-center bg-cover"
                  style={{ backgroundImage: `url(${slideBackgroundImage})` }}
                >
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
                  <div
                    className="absolute bottom-0 left-0 right-0 h-48 pointer-events-none"
                    style={{
                      background:
                        'linear-gradient(to top, #141413 0%, rgba(20,20,19,0.92) 30%, rgba(20,20,19,0.7) 55%, rgba(20,20,19,0.3) 80%, transparent 100%)',
                    }}
                  />
                </div>

                {/* Overlay Content - Left-aligned */}
                <div className="absolute inset-0 flex flex-col justify-center items-start container mx-auto px-6 z-10">
                  {channelLogo ? (
                    <img
                      src={channelLogo}
                      alt={slideTitle}
                      className="featured-carousel-logo mb-2 h-auto max-w-md object-contain"
                    />
                  ) : (
                    <h1 className="mb-2 text-4xl font-bold text-white md:text-6xl font-display">
                      {slideTitle}
                    </h1>
                  )}

                  <HeroSlideEventDate channel={channel} cmsReady={cmsReady} />

                  {/* Metadata badges */}
                  <div className="mb-4 flex flex-wrap gap-2">
                    {slideYear && <span className="hero-badge">{slideYear}</span>}
                    {slideRating && <span className="hero-badge">{slideRating}</span>}
                    {slideDuration && <span className="hero-badge">{slideDuration}</span>}
                    {slideProgramType && <span className="hero-badge">{slideProgramType}</span>}
                  </div>

                  {/* Inline Carousel Dots (below title/date metadata) */}
                  {channels.length > 1 && (
                    <div
                      className="featured-inline-dots mb-5"
                      role="tablist"
                      aria-label="Featured slides"
                    >
                      {channels.map((_, dotIndex) => (
                        <button
                          key={`hero-dot-${dotIndex}`}
                          type="button"
                          className={`featured-inline-dot ${
                            dotIndex === currentIndex ? 'is-active' : ''
                          }`}
                          onClick={() => {
                            setCurrentIndex(dotIndex);
                            sliderRef.current?.slickGoTo(dotIndex);
                          }}
                          aria-label={`Go to slide ${dotIndex + 1}`}
                          aria-selected={dotIndex === currentIndex}
                        />
                      ))}
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-4 mb-6">
                    <HeroSlideWatchButton
                      channel={channel}
                      cmsReady={cmsReady}
                      onClick={() => {
                        setCurrentIndex(index);
                        handleWatchNowClick(channel);
                      }}
                      onWatchIn={() => {
                        setCurrentIndex(index);
                        handleLearnMoreClick(channel);
                      }}
                    />
                    <button
                      type="button"
                      className="featured-hero-cta flex items-center px-6 py-3 font-bold text-white bg-white/20 hover:bg-white/30 border border-white/30 font-display text-lg uppercase tracking-wide"
                      onClick={() => {
                        setCurrentIndex(index);
                        handleLearnMoreClick(channel);
                      }}
                    >
                      Learn More
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </Slider>
      ) : null}

      {/* PDP Modal */}
      {showPDPModal && selectedChannel && (
        <PDPModal
          channel={selectedChannel}
          isVisible={showPDPModal}
          onClose={handleClosePDPModal}
        />
      )}
    </div>
  );
};

export default FeaturedHeroCarousel;
